import {
  CustomColumnHeader,
  CustomColumnHeaderDataType,
  getAdderColumnType,
  getDataTypes,
  getDescriptiveColumnType,
  getQualifyingColumnType,
} from 'types';

/** Default maximum number of adder columns per lot. */
export const DEFAULT_MAX_ADDER_COLUMN_COUNT = 10;

/**
 * Returns true if the column is descriptive.
 *
 * A missing `column_type` is treated as descriptive to match the fallback used by
 * `createColumnSettings` and `sortByOrder`. Callers that need a strict match (e.g. counting
 * descriptive columns against a limit) should compare `column_type` directly instead.
 */
export const isDescriptiveColumn = (header: CustomColumnHeader): boolean =>
  !header.column_type || header.column_type === getDescriptiveColumnType();

/**
 * Sorts columns by type order (descriptive < qualifying < adder),
 * then by column_order_index within the same type.
 */
export const sortByOrder = (a: CustomColumnHeader, b: CustomColumnHeader): number => {
  const qualifyingType = getQualifyingColumnType();

  const getTypeSortOrder = (h: CustomColumnHeader): number => {
    if (isDescriptiveColumn(h)) return 0;
    if (h.column_type === qualifyingType) return 1;
    return 2; // adder (and any future types)
  };

  const orderDiff = getTypeSortOrder(a) - getTypeSortOrder(b);
  if (orderDiff !== 0) return orderDiff;

  return a.column_order_index - b.column_order_index;
};

const isValidColumnType = (header: CustomColumnHeader, isSupplier: boolean): boolean => {
  if (!header.column_type?.length) return true;

  const descriptiveType = getDescriptiveColumnType();
  const qualifyingType = getQualifyingColumnType();

  return header.column_type === descriptiveType ||
    (header.column_type === qualifyingType && isSupplier);
};

const shouldIncludeDestroyed = (header: CustomColumnHeader, filterDestroyed: boolean): boolean => {
  // Not filtering out deleted columns here, because we need it for the UI.
  return filterDestroyed ? !header._destroy : true;
};

export const getColumns = ({ filterDestroyed = false, headers, isSupplier = false, types: _types }: {
  filterDestroyed?: boolean;
  headers: CustomColumnHeader[];
  isSupplier?: boolean;
  types?: CustomColumnHeaderDataType[];
}): CustomColumnHeader[] => {
  if (!headers?.length) return [];

  return headers.filter((header: CustomColumnHeader) => (
    isValidColumnType(header, isSupplier) &&
    shouldIncludeDestroyed(header, filterDestroyed)
  )).sort(sortByOrder);
};

interface GetDescriptiveAndQualifyingColumnsParams {
  headers: CustomColumnHeader[];
  isSupplier: boolean;
  maxDescriptiveColumnCount: number;
}

export const getDescriptiveAndQualifyingColumns = ({ headers, isSupplier, maxDescriptiveColumnCount }: GetDescriptiveAndQualifyingColumnsParams): CustomColumnHeader[] => {
  const columns = getColumns({ headers, isSupplier, types: getDataTypes() });

  // If the column is hidden from the supplier, don't show it in the supplier view.
  if (isSupplier) {
    return columns.filter((column: CustomColumnHeader) => column.is_supplier_visible);
  }

  if (!maxDescriptiveColumnCount || columns.length <= maxDescriptiveColumnCount) {
    return columns;
  }

  // If more than the maximum number of descriptive columns are used, do not show them all.
  // This should never happen, but it is a safeguard.
  return columns.splice(0, maxDescriptiveColumnCount);
};

/**
 * Returns adder columns sorted by column_order_index.
 * Separate from getDescriptiveAndQualifyingColumns to avoid maxDescriptiveColumnCount truncation.
 */
export const getAdderColumns = (headers: CustomColumnHeader[]): CustomColumnHeader[] => {
  if (!headers?.length) return [];
  const adderType = getAdderColumnType();
  return headers
    .filter((h: CustomColumnHeader) => h.column_type === adderType && !h._destroy)
    .sort((a, b) => a.column_order_index - b.column_order_index);
};
